<template>
    <div v-if="shouldRender && noInlineAlert">
        <span class="status-message" data-qa="ktv-verification-status-message">
            <KtvSmsVerificationStatusAlertMessage
                :days-to-resubmit="daysToResubmit"
                :message="message"
                :number-type="numberType"
                :rejection-date="rejectionDate"
                :verification-status="verificationStatus"
            />
        </span>

        <div v-if="isRejected && message" class="rejection-reason-container">
            <span class="rejection-reason-value">{{ $t('sms.verification.rejectionReason') }} </span>
            <span data-qa="ktv-verification-rejection-reason">{{ message }}</span>
        </div>

        <div v-if="shouldShowButton" class="ktv-unable-to-send-controls">
            <DsFilledButton
                data-qa="ktv-verification-status-action-button"
                @click="handleActionButtonClick"
            >
                {{ $t(i18nLabelButton) }}
            </DsFilledButton>
        </div>
    </div>

    <DsInlineAlert
        v-else-if="shouldRender"
        leading-icon
        class="alert"
        :class="{ 'squared' : squared }"
        :type="i18nKeyAlertType"
    >
        <template #default>
            <span class="status-message" data-qa="ktv-verification-status-message">
                <KtvSmsVerificationStatusAlertMessage
                    :message="message"
                    :number-type="numberType"
                    :rejection-date="rejectionDate"
                    :verification-status="verificationStatus"
                />
            </span>

            <div v-if="isRejected && message" class="rejection-reason-container">
                <span class="rejection-reason-value">{{ $t('sms.verification.rejectionReason') }} </span>
                <span data-qa="ktv-verification-rejection-reason">{{ message }}</span>
            </div>
        </template>

        <template v-if="shouldShowButton" #footer>
            <DsFilledButton
                data-qa="ktv-verification-status-action-button"
                dense
                gray
                @click="handleActionButtonClick"
            >
                {{ i18nLabelButton }}
            </DsFilledButton>
        </template>
    </DsInlineAlert>
</template>


<script>
import KtvSmsVerificationStatusAlertMessage from './KtvSmsVerificationStatusAlertMessage';
import { getResubmissionDaysRemaining } from './automatedSms.util';
import {
    KEAP_SMS_COMPLIANCE_URL,
    KMN_VERIFICATION_STATUS_TYPE as STATUSES,
} from '@/components/broadcast/constants/smsBroadcast.constants';
import {
    SENDABLE_SMS_VERIFICATION_STATUSES_POST_20240130,
} from '@/components/automatedSms/sms.constants';


const ALERT_TYPES = {
    [STATUSES.APPROVED]: 'good',
    [STATUSES.EXPIRED]: 'critical',
    [STATUSES.NOT_VERIFIED]: 'info',
    [STATUSES.PENDING_SUBMISSION]: 'info',
    [STATUSES.PENDING_VERIFICATION]: 'info',
    [STATUSES.REJECTED]: 'critical',
    [STATUSES.RESUBMISSION]: 'critical',
};

const TOLL_FREE = 'TOLL_FREE';

export const KTV_VERIFICATION_ALERT_SOURCES = {
    KTV_VERIFICATION_FORM: 'KTV_VERIFICATION_FORM',
    KTV_SETTINGS: 'KTV_SETTINGS',
};

export const KTV_VERIFICATION_ALERT_SOURCE_LIST = Object.values(KTV_VERIFICATION_ALERT_SOURCES);

export const TEXT_MESSAGE_VERIFICATION_SETTINGS_URI = '/app/miscSetting/itemWrapper?systemId=nav.marketing&settingModuleName=Marketing&settingTabName=text.verification.settings.label';

export default {
    components: { KtvSmsVerificationStatusAlertMessage },

    props: {
        daysToResubmit: {
            type: Number,
            default: 7,
        },
        message: {
            type: String,
            default: '',
        },
        noInlineAlert: Boolean,
        numberType: {
            type: String,
            default: TOLL_FREE,
        },
        renderIfSendingBlocked: Boolean,
        rejectionDate: {
            type: String,
            default: '',
        },
        showButton: Boolean,
        source: String,
        squared: Boolean,
        verificationStatus: {
            required: true,
            type: String,
            default: '',
        },
    },

    emits: ['action'],

    computed: {
        daysRemaining() {
            return getResubmissionDaysRemaining(this.rejectionDate);
        },

        i18nKeyAlertType() {
            if (this.verificationStatus === STATUSES.RESUBMISSION && this.daysRemaining < 1) {
                return ALERT_TYPES.EXPIRED;
            }

            return ALERT_TYPES[this.verificationStatus] || 'info';
        },

        i18nLabelButton() {
            if (this.isRejectedButton) {
                return this.$t('sms.verification.button.acceptableUsePolicy');
            }

            return this.$t(['', STATUSES.NOT_VERIFIED].includes(this.verificationStatus)
                ? 'sms.verification.button.submitVerificationForm'
                : 'sms.verification.button.textAndVoiceSettings');
        },

        isStatusBlocking() {
            return this.verificationStatus
                && !this.sendableStatuses.includes(this.verificationStatus);
        },

        isRejected() {
            return [STATUSES.EXPIRED, STATUSES.REJECTED, STATUSES.RESUBMISSION]
                .includes(this.verificationStatus);
        },

        isRejectedButton() {
            return KTV_VERIFICATION_ALERT_SOURCE_LIST.includes(this.source)
                && this.verificationStatus === STATUSES.REJECTED;
        },

        sendableStatuses() {
            return SENDABLE_SMS_VERIFICATION_STATUSES_POST_20240130;
        },

        shouldRender() {
            return this.verificationStatus && (!this.renderIfSendingBlocked || this.isStatusBlocking);
        },

        shouldShowButton() {
            if (this.verificationStatus !== STATUSES.REJECTED && this.source === 'KTV_SETTINGS') {
                return false;
            }

            return this.showButton;
        },
    },

    methods: {
        handleActionButtonClick() {
            if (this.isRejectedButton) {
                window.open(KEAP_SMS_COMPLIANCE_URL, '_blank', 'noopener,noreferrer');
            } else {
                window.location = TEXT_MESSAGE_VERIFICATION_SETTINGS_URI;
            }

            this.$emit('action');
        },
    },
};
</script>


<style lang="scss" scoped>
.alert {
    width: 100%;
}

.squared {
    border-radius: 0;
}

.status-message {
    font-size: $font-size-sm;
    line-height: $line-height-md;
}

.ktv-unable-to-send-controls {
    display: flex;
    margin-top: $spacing-200;
}

.rejection-reason-container  {
    font-size: $font-size-sm;
    margin-top: $spacing-100;
}

.rejection-reason-value {
    font-weight: $font-weight-bold;
}
</style>
